import React, { useState } from 'react';
import { TableItem, TransformedGroup } from '../../props/request';
import { useTranslation } from '../../utils/i18n';
import DocsIcon from '../icons/DocsIcon';
import SortIcon from '../icons/SortIcon';
import TableNoDataIcon from '../icons/TableNoDataIcon';
import './UseTable.css';

interface Column {
  key: string;
  label: string;
}

type TableGroup = TransformedGroup;

interface TableProps {
  columns: Column[];
  datas: TableGroup[];
  actionId?: string;
  onHandleAction?: (item: TableItem) => void;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onDataChange?: (updatedData: TableGroup[]) => void;
  sortField?: string;
  sortDirection?: string;
}

const Table: React.FC<TableProps> = ({
  columns,
  datas,
  actionId = "",
  onHandleAction,
  onSort,
  onDataChange,
  sortField,
  sortDirection
}) => {
  const { t, currentLocale } = useTranslation();
  const [tableData, setTableData] = useState<TableGroup[]>(datas);

  // Update local state when props change, but preserve checkbox states
  React.useEffect(() => {
    setTableData(prevData => {
      // If this is the first load or data structure changed significantly
      if (prevData.length === 0 || prevData.length !== datas.length) {
        return datas;
      }

      // Preserve checkbox states when updating with new data
      return datas.map(newGroup => {
        const existingGroup = prevData.find(g => g.id === newGroup.id);
        if (existingGroup) {
          return {
            ...newGroup,
            checked: existingGroup.checked,
            items: newGroup.items.map(newItem => {
              const existingItem = existingGroup.items.find(i => i.id === newItem.id);
              return {
                ...newItem,
                checked: existingItem ? existingItem.checked : false
              };
            })
          };
        }
        return newGroup;
      });
    });
  }, [datas]);

  // Helper function to determine if a column is currently being sorted
  const isColumnActive = (columnKey: string): boolean => {
    if (columnKey === 'title') {
      return sortField === 'DATA_OPEN_NAME_TH' || sortField === 'DATA_OPEN_NAME_EN';
    } else if (columnKey === 'status') {
      return sortField === 'DATA_OPEN_STATUS';
    }
    return false;
  };

  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey, 'asc'); // Let parent handle direction logic
    }
  };

  const handleGroupCheckboxChange = (groupId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    const updatedData = tableData.map((group) => {
      if (group.id === groupId) {
        return {
          ...group,
          checked: isChecked,
          items: group.items.map((item) => ({
            ...item,
            checked: isChecked
          }))
        };
      }
      return group;
    });

    setTableData(updatedData);
    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  const handleItemCheckboxChange = (
    groupId: string,
    itemId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isChecked = event.target.checked;

    const updatedData = tableData.map((group) => {
      if (group.id === groupId) {
        const updatedItems = group.items.map((item) => {
          if (item.id === itemId) {
            return { ...item, checked: isChecked };
          }
          return item;
        });

        // Check if all items are checked to update group checkbox
        const allItemsChecked = updatedItems.every((item) => item.checked);

        return {
          ...group,
          checked: allItemsChecked,
          items: updatedItems
        };
      }
      return group;
    });

    setTableData(updatedData);
    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  const getStatusClass = (data: any): string => {
    const status = data.srcStatus;
    const requestStatus = data.requestStatus;
    let warningDays = null;
    if (data.warningDay) {
      warningDays = data.warningDay;
    }

    const remainingDays = data.remainingDays;
    if (requestStatus === "approved") {
      if (warningDays && remainingDays <= warningDays) {
        return 'status-expired-soon';
      }
      return 'status-active';

    }
    return 'status-active';
  };

  const handleActionClick = (item: TableItem) => {
    if (onHandleAction) {
      onHandleAction(item);
    }
  };

  // Format date to Thai Buddhist calendar (dd/mm/yyyy)
  const formatDateToThai = (dateString: string): string => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear() + 543; // Convert to Thai Buddhist year

      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="table-scroll-container">
      <div className="wrapper_table-main">
        <table className="table table-request">
          <thead>
            <tr>
              <th className="checkboxGroup"></th>
              <th className="checkboxItem"></th>
              {columns.map((col) => (
                <th key={col.key} className="sortable-header">
                  <div
                    className="header-content"
                    onClick={() => handleSort(col.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort(col.key)}
                  >
                    <span className="text-neutral-70 fw-normal" style={{ fontSize: '14px' }}>
                      {col.label}
                    </span>
                    <SortIcon
                      isActive={isColumnActive(col.key)}
                      direction={sortDirection === 'DESC' ? 'desc' : 'asc'}
                      onClick={() => handleSort(col.key)}
                    />
                  </div>
                </th>
              ))}
              <th className="action"></th>
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 3} className="table-request-no-data">
                  <TableNoDataIcon />
                </td>
              </tr>
            ) : (
              tableData.map((group: TableGroup) => (
                <React.Fragment key={group.id}>
                  <tr className="table-request-row-group">
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={group.checked}
                        onChange={(e) => handleGroupCheckboxChange(group.id, e)}
                      />
                    </td>
                    <td colSpan={columns.length + 1}>
                      <div>{group.label}</div>
                    </td>
                    <td className="action"></td>
                  </tr>
                  {group.items.map((item: TableItem) => (
                    <tr key={item.id} className="table-request-row-item">
                      <td></td>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={item.checked}
                          onChange={(e) => handleItemCheckboxChange(group.id, item.id, e)}
                        />
                      </td>
                      <td>{currentLocale === "th-TH" ? item.data_open_name_th : item.data_open_name_en}</td>
                      <td style={{ textAlign: 'center' }}>{formatDateToThai(item.updatedate || '')}</td>
                      <td style={{ textAlign: 'center' }}>{item.version || ''}</td>
                      <td className="td-status">
                        <div>
                          <span className={`status-badge ${getStatusClass(item)}`}>
                            {item.status}
                          </span>
                          {item.version_changed_flag && (
                            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                              {t('requestdata.request.table.versionWarning', { date: formatDateToThai(item.updatedate || '') })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="action">
                        <button
                          type="button"
                          className="hide-button"
                          // data-bs-toggle="modal"
                          // data-bs-target={actionId}
                          onClick={() => handleActionClick(item)}
                        >
                          <DocsIcon />
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
